import ExcelJS from 'exceljs';
import type { NeisLearningContentUploadRow, NeisUploadLearningContentRow } from './neisLearningContentUploadRows';
import type { NeisLearningContentUploadViewMode } from './neisLearningContentUploadRows';

const HEADERS_ANNUAL_PLAN_WITHOUT_DATE = [
  '순서',
  '학년',
  '학기',
  '편제',
  '단원',
  '프로그램 내용',
  '쪽수',
  '보조쪽수',
  '해당차시',
  '전체차시',
  '준비물',
] as const;

const HEADERS_NEIS_UPLOAD = [
  '순서',
  '학년',
  '학기',
  '편제',
  '단원',
  '학습내용',
  '쪽수',
  '보조쪽수',
  '해당차시',
  '전체차시',
  '준비물',
] as const;

const HEADERS_WITH_DATE = [
  '순서',
  '날짜',
  '요일',
  '교시',
  '학년',
  '학기',
  '편제',
  '단원',
  '프로그램 내용',
  '쪽수',
  '보조쪽수',
  '해당차시',
  '전체차시',
  '준비물',
] as const;

const COLUMN_WIDTH_PERCENT_WITHOUT_DATE = [4, 6, 5, 8, 11, 28, 6, 6, 4.5, 4.5, 11] as const;
const COLUMN_WIDTH_PERCENT_WITH_DATE = [3.5, 7, 3.5, 4, 5, 4.5, 7, 9, 22, 5, 5, 4, 4, 9] as const;

const EXCEL_BASE_TOTAL_WCH = 100;
const HEADER_ROW_INDEX = 2;
const DATA_START_ROW_INDEX = 3;
const PERIOD_NUMBER_FORMAT = '0';

function solidCellBorder(): Partial<ExcelJS.Borders> {
  const side: ExcelJS.Border = { style: 'thin', color: { argb: 'FF000000' } };
  return {
    top: { ...side },
    left: { ...side },
    bottom: { ...side },
    right: { ...side },
  };
}

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFF4F4F4' },
};

function displayWidthOfText(text: string): number {
  let width = 0;
  for (const ch of text) {
    width += ch.charCodeAt(0) > 255 ? 2 : 1;
  }
  return width;
}

function rowToCells(
  row: NeisLearningContentUploadRow | NeisUploadLearningContentRow,
  displayIndex: number,
  showDateColumns: boolean,
  viewMode: NeisLearningContentUploadViewMode
): string[] {
  const seq =
    viewMode === 'neis-upload' && 'trackSeq' in row && Number.isFinite(row.trackSeq)
      ? String(row.trackSeq)
      : String(displayIndex);

  if (showDateColumns && viewMode === 'annual-plan') {
    return [
      seq,
      row.dateLabel,
      row.weekdayLabel,
      row.periodLabel,
      row.gradeLabel,
      row.semesterLabel,
      row.templateLabel,
      row.unit,
      row.learning,
      row.page,
      row.assistantPage,
      row.currentPeriod,
      row.totalPeriod,
      row.material,
    ];
  }

  return [
    seq,
    row.gradeLabel,
    row.semesterLabel,
    row.templateLabel,
    row.unit,
    row.learning,
    row.page,
    row.assistantPage,
    row.currentPeriod,
    row.totalPeriod,
    row.material,
  ];
}

function buildColumnWidths(
  headers: readonly string[],
  dataRows: string[][],
  columnWidthPercent: readonly number[]
): number[] {
  const allRows = [headers as unknown as string[], ...dataRows];
  const percentSum = columnWidthPercent.reduce((sum, pct) => sum + pct, 0);

  return columnWidthPercent.map((pct, colIndex) => {
    const cssWch = (pct / percentSum) * EXCEL_BASE_TOTAL_WCH;
    const contentWch = Math.max(
      ...allRows.map((row) => displayWidthOfText((row[colIndex] ?? '').trim())),
      2
    );
    return Math.min(72, Math.max(cssWch, contentWch + 1.5));
  });
}

function triggerBrowserDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function parsePeriodNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

function periodColumnIndexes(showDateColumns: boolean): { seq: number; current: number; total: number } {
  if (showDateColumns) {
    return { seq: 0, current: 11, total: 12 };
  }
  return { seq: 0, current: 8, total: 9 };
}

function setDataCellValue(
  cell: ExcelJS.Cell,
  colOffset: number,
  value: string,
  periodCols: { seq: number; current: number; total: number }
): void {
  if (colOffset === periodCols.seq || colOffset === periodCols.current || colOffset === periodCols.total) {
    const num = parsePeriodNumber(value);
    if (num !== null) {
      cell.value = num;
      cell.numFmt = PERIOD_NUMBER_FORMAT;
      return;
    }
  }
  cell.value = value;
}

function applyTableCellStyle(cell: ExcelJS.Cell, isHeader: boolean): void {
  cell.border = solidCellBorder();
  if (isHeader) {
    cell.font = { bold: true };
    cell.fill = HEADER_FILL;
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    return;
  }
  cell.alignment = { vertical: 'top', wrapText: true };
}

function styleTableRange(sheet: ExcelJS.Worksheet, lastRow: number, columnCount: number): void {
  for (let rowIndex = HEADER_ROW_INDEX; rowIndex <= lastRow; rowIndex += 1) {
    for (let colIndex = 1; colIndex <= columnCount; colIndex += 1) {
      const cell = sheet.getCell(rowIndex, colIndex);
      applyTableCellStyle(cell, rowIndex === HEADER_ROW_INDEX);
    }
  }
}

export async function saveNeisLearningContentUploadAsExcel(params: {
  rows: Array<NeisLearningContentUploadRow | NeisUploadLearningContentRow>;
  headerTitle: string;
  termNumber: 1 | 2;
  showDateColumns: boolean;
  viewMode?: NeisLearningContentUploadViewMode;
  fileName: string;
  sheetName?: string;
}): Promise<void> {
  const {
    rows,
    headerTitle,
    termNumber,
    showDateColumns,
    viewMode = 'annual-plan',
    fileName,
    sheetName = viewMode === 'neis-upload' ? 'NEIS학습내용' : '연간지도계획',
  } = params;
  if (rows.length === 0) return;

  const withDate = viewMode === 'annual-plan' && showDateColumns;
  const headers = withDate
    ? HEADERS_WITH_DATE
    : viewMode === 'neis-upload'
      ? HEADERS_NEIS_UPLOAD
      : HEADERS_ANNUAL_PLAN_WITHOUT_DATE;
  const columnWidthPercent = withDate ? COLUMN_WIDTH_PERCENT_WITH_DATE : COLUMN_WIDTH_PERCENT_WITHOUT_DATE;
  const periodCols = periodColumnIndexes(withDate);
  const dataRows = rows.map((row, index) => rowToCells(row, index + 1, withDate, viewMode));
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);
  const columnCount = headers.length;

  const columnWidths = buildColumnWidths(headers, dataRows, columnWidthPercent);
  sheet.columns = columnWidths.map((width) => ({ width }));

  sheet.mergeCells(1, 1, 1, columnCount);
  const metaCell = sheet.getCell(1, 1);
  metaCell.value = `${termNumber}학기 · ${headerTitle}`;
  metaCell.font = { bold: true, size: 12 };
  metaCell.alignment = { horizontal: 'center', vertical: 'middle' };

  const headerRow = sheet.getRow(HEADER_ROW_INDEX);
  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    applyTableCellStyle(cell, true);
  });
  headerRow.height = 28;

  dataRows.forEach((cells, rowOffset) => {
    const row = sheet.getRow(DATA_START_ROW_INDEX + rowOffset);
    cells.forEach((value, colOffset) => {
      const cell = row.getCell(colOffset + 1);
      setDataCellValue(cell, colOffset, value, periodCols);
    });
  });

  const lastRow = DATA_START_ROW_INDEX + dataRows.length - 1;
  styleTableRange(sheet, lastRow, columnCount);

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBrowserDownload(buffer, fileName);
}
